import React from "react";
import { track, events } from "../lib/analytics";
import { reportClientError } from "../lib/api";

// App-wide error boundary. Catches render-time errors anywhere in the React
// tree and renders a recoverable fallback instead of leaving a blank screen.
//
// Iter-275.AB (Phase K — network/error UX) — Now also surfaces every caught
// error to GA4 + PostHog via the new `events.UNHANDLED_ERROR` event so the
// admin team learns about production render failures BEFORE a user emails in.
//
// Iter-275.AE — Also POST to /api/client-errors so React render errors flow
// into the SAME dedup'd admin HealthCenterPanel "JS errors (7d)" surface as
// window.onerror does (iter-231's errorCapture.js handles non-React errors;
// React's error system intercepts render errors BEFORE they bubble to
// window.onerror, so the boundary is the only place to capture them).
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Log so we can see it in the browser console + any analytics hook.
    // Intentionally not silenced — we want clear signal during incidents.
    // eslint-disable-next-line no-console
    console.error("[CarSavr] Render error caught by boundary:", error, info);
    const firstStackLine = (info?.componentStack || "")
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean)[0] || null;
    const pagePath =
      typeof window !== "undefined" ? window.location.pathname : "/";
    // Iter-275.AB — GA4 analytics so prod render errors show up in dashboards.
    // Wrapped in try/catch because the boundary MUST NOT throw (it would
    // re-mount itself in a render loop).
    try {
      track(events.UNHANDLED_ERROR, {
        name: String(error?.name || "Error").slice(0, 80),
        message: String(error?.message || "").slice(0, 240),
        component: firstStackLine ? firstStackLine.slice(0, 120) : null,
        page_path: pagePath,
      });
    } catch {
      /* swallow — error reporting must never throw */
    }
    // Iter-275.AE — Backend ingest with stack-hash dedup. Flows into the
    // existing /admin/health-center "JS errors (7d)" table so operators see
    // React render errors alongside window.onerror data without context-
    // switching to GA4.
    try {
      reportClientError({
        message: String(error?.message || error?.name || "Render error").slice(0, 500),
        stack: error?.stack
          ? String(error.stack).slice(0, 4000)
          : (info?.componentStack ? String(info.componentStack).slice(0, 4000) : null),
        url: typeof window !== "undefined" ? window.location.href : null,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
    } catch {
      /* swallow — error reporting must never throw */
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <main
        data-testid="error-boundary-fallback"
        className="bg-[#FAFAFA] min-h-[80vh] grid place-items-center py-24"
      >
        <div className="max-w-[560px] w-full px-6 lg:px-10 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#10B981] mb-5">
            Something went sideways
          </p>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter leading-[0.95] text-slate-950 mb-5">
            We hit a bump. Reloading usually fixes it.
          </h1>
          <p className="text-base text-slate-600 leading-relaxed mb-10 max-w-md mx-auto">
            CarSavr couldn&apos;t render this page. We&apos;ve logged the issue.
            A quick reload almost always resolves it — if not, head home.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              data-testid="error-boundary-reload"
              className="inline-flex items-center gap-2 bg-[#0A2540] hover:bg-slate-900 text-white font-bold h-12 px-6 transition-all hover:-translate-y-0.5"
            >
              Reload page
            </button>
            <a
              href="/"
              data-testid="error-boundary-home"
              className="inline-flex items-center gap-2 border border-slate-300 hover:border-slate-950 text-slate-950 font-semibold h-12 px-6 transition-colors"
            >
              Back to CarSavr
            </a>
          </div>
        </div>
      </main>
    );
  }
}
